import { Component, Input, OnChanges } from '@angular/core';
import { ChartConfiguration, ChartData } from 'chart.js';
import { SolarHistoryPoint } from '../version.service';

@Component({
  selector: 'app-solar-chart',
  template: `
    <div style="position:relative;height:clamp(200px,55vh,700px);">
      <canvas baseChart
        [data]="chartData"
        [options]="chartOptions"
        type="line">
      </canvas>
    </div>
  `,
})
export class SolarChartComponent implements OnChanges {
  @Input() history: SolarHistoryPoint[] = [];
  @Input() darkMode = false;
  @Input() labelPv      = '';
  @Input() labelBattery = '';
  @Input() labelGrid    = '';
  @Input() labelTotal   = '';

  chartData: ChartData<'line'> = { datasets: [] };
  chartOptions: ChartConfiguration<'line'>['options'] = {};

  private formatW(w: number): string {
    const abs = Math.abs(w);
    return abs >= 1000 ? `${(w / 1000).toFixed(2)} kW` : `${Math.round(w)} W`;
  }

  ngOnChanges(): void {
    const textColor     = this.darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';
    const gridColor     = this.darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
    const zeroLineColor = this.darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)';

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              const ts = items[0]?.parsed.x;
              return ts != null
                ? new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
                : '';
            },
            label: (item) => ` ${item.dataset.label}: ${this.formatW(item.parsed.y ?? 0)}`,
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: Date.now() - 24 * 3600 * 1000,
          max: Date.now(),
          ticks: {
            color: textColor,
            maxTicksLimit: 9,
            callback: (val) => {
              const d = new Date(val as number);
              return d.getMinutes() === 0 && d.getHours() % 3 === 0
                ? `${String(d.getHours()).padStart(2, '0')}:00`
                : null;
            },
          },
          grid: { color: gridColor },
        },
        y: {
          ticks: {
            color: textColor,
            callback: (val) => {
              const w = val as number;
              return Math.abs(w) >= 1000 ? `${(w / 1000).toFixed(1)} kW` : `${w} W`;
            },
          },
          grid: {
            color: (ctx) => ctx.tick.value === 0 ? zeroLineColor : gridColor,
          },
        },
      },
      elements: {
        point: { radius: 0, hoverRadius: 3 },
        line:  { borderWidth: 1.5, tension: 0.6 },
      },
    };

    this.chartData = {
      datasets: [
        {
          label: `☀️ ${this.labelPv}`,
          data: this.history.map(p => ({ x: p.ts, y: p.pvYieldW })),
          borderColor: '#f59e0b',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `🔋 ${this.labelBattery}`,
          data: this.history.map(p => ({ x: p.ts, y: p.batteryDischargeW })),
          borderColor: '#22c55e',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `🔌 ${this.labelGrid}`,
          data: this.history.map(p => ({ x: p.ts, y: p.gridConsumptionW })),
          borderColor: '#ef4444',
          backgroundColor: 'transparent',
          fill: false,
        },
        {
          label: `⚡ ${this.labelTotal}`,
          data: this.history.map(p => ({ x: p.ts, y: p.totalConsumptionW })),
          borderColor: '#3b82f6',
          backgroundColor: 'transparent',
          fill: false,
        },
      ],
    };
  }
}
